<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { resolveMaximumFileUploadSize } from 'shared/helpers/FileHelper';
import {
  MAX_CANNED_RESPONSE_IMAGES,
  CANNED_RESPONSE_IMAGE_TYPES,
} from 'dashboard/helper/cannedResponseHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  response: { type: Object, default: () => ({}) },
  onClose: { type: Function, default: () => {} },
});

const { t } = useI18n();
const store = useStore();
const globalConfig = useMapGetter('globalConfig/get');
const shortCode = ref(props.response.short_code || '');
const content = ref(props.response.content || '');
const images = ref([...(props.response.images || [])]);
const show = ref(true);
const isSaving = ref(false);
const imageInput = ref(null);
const title = computed(() =>
  props.response.id
    ? `${t('CANNED_MGMT.EDIT.TITLE')} - ${props.response.short_code}`
    : t('CANNED_MGMT.ADD.TITLE')
);
const maxSize = computed(() =>
  resolveMaximumFileUploadSize(globalConfig.value?.maximumFileUploadSize)
);
const rules = {
  shortCode: { required, minLength: minLength(2) },
  content: { required: requiredIf(() => !images.value.length) },
};
const v$ = useVuelidate(rules, { shortCode, content });

const addImages = event => {
  const files = [...event.target.files];
  event.target.value = '';
  if (images.value.length + files.length > MAX_CANNED_RESPONSE_IMAGES) {
    useAlert(
      t('CANNED_MGMT.IMAGES.COUNT_LIMIT', { count: MAX_CANNED_RESPONSE_IMAGES })
    );
    return;
  }
  if (
    files.some(
      file => !CANNED_RESPONSE_IMAGE_TYPES.includes(file.type) || !file.size
    )
  ) {
    useAlert(t('CANNED_MGMT.IMAGES.INVALID_TYPE'));
    return;
  }
  if (files.some(file => file.size > maxSize.value * 1024 * 1024)) {
    useAlert(t('CANNED_MGMT.IMAGES.SIZE_LIMIT', { size: maxSize.value }));
    return;
  }
  images.value.push(
    ...files.map(file => ({
      file,
      filename: file.name,
      url: URL.createObjectURL(file),
    }))
  );
};

const removeImage = index => {
  const [image] = images.value.splice(index, 1);
  if (image.file) URL.revokeObjectURL(image.url);
};

onBeforeUnmount(() => {
  images.value.forEach(image => {
    if (image.file) URL.revokeObjectURL(image.url);
  });
});

const save = async () => {
  if (isSaving.value || !(await v$.value.$validate())) return;
  isSaving.value = true;
  const retainedIds = images.value.map(image => image.id);
  const payload = {
    short_code: shortCode.value.trim(),
    content: content.value,
    images: images.value.filter(image => image.file).map(image => image.file),
    removedImageIds: (props.response.images || [])
      .filter(image => !retainedIds.includes(image.id))
      .map(image => image.id),
  };
  try {
    if (props.response.id) {
      await store.dispatch('updateCannedResponse', {
        id: props.response.id,
        ...payload,
      });
    } else {
      await store.dispatch('createCannedResponse', payload);
    }
    useAlert(
      props.response.id
        ? t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE')
        : t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE')
    );
    props.onClose();
  } catch (error) {
    useAlert(
      error?.message ||
        (props.response.id
          ? t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE')
          : t('CANNED_MGMT.ADD.API.ERROR_MESSAGE'))
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="title" />
      <form class="flex flex-col w-full gap-3" @submit.prevent="save">
        <label :class="{ error: v$.shortCode.$error }">
          {{ t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
          <input
            v-model="shortCode"
            type="text"
            :disabled="isSaving"
            :placeholder="t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
            @blur="v$.shortCode.$touch"
          />
        </label>
        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1 [&_.ProseMirror-menubar]:hidden [&_.ProseMirror-woot-style]:min-h-[8rem]"
              :class="{ editor_warning: v$.content.$error }"
              :disabled="isSaving"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
          <p class="mt-2 mb-0 text-xs text-n-slate-11">
            {{ t('CANNED_MGMT.IMAGES.HELP') }}
          </p>
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-2">
            <label class="mb-0">{{ t('CANNED_MGMT.IMAGES.LABEL') }}</label>
            <NextButton
              type="button"
              icon="i-lucide-image-plus"
              :label="t('CANNED_MGMT.IMAGES.ADD')"
              :disabled="
                isSaving || images.length >= MAX_CANNED_RESPONSE_IMAGES
              "
              faded
              sm
              @click="imageInput.click()"
            />
            <input
              ref="imageInput"
              type="file"
              class="hidden"
              :aria-label="t('CANNED_MGMT.IMAGES.ADD')"
              :accept="CANNED_RESPONSE_IMAGE_TYPES.join(',')"
              :disabled="isSaving"
              multiple
              @change="addImages"
            />
          </div>
          <div v-if="images.length" class="flex flex-wrap gap-2">
            <div
              v-for="(image, index) in images"
              :key="image.url"
              class="relative flex flex-col gap-1 w-24 rounded-lg border border-n-weak p-1"
            >
              <img
                :src="image.url"
                :alt="image.filename"
                class="w-full h-20 object-contain rounded bg-n-slate-2"
              />
              <span
                class="text-xs text-n-slate-11 truncate"
                :title="image.filename"
              >
                {{ image.filename }}
              </span>
              <NextButton
                type="button"
                icon="i-lucide-x"
                class="absolute top-1 end-1"
                :aria-label="
                  t('CANNED_MGMT.IMAGES.REMOVE', { name: image.filename })
                "
                :disabled="isSaving"
                faded
                slate
                xs
                @click="removeImage(index)"
              />
            </div>
          </div>
          <p class="mb-0 text-xs text-n-slate-11">
            {{
              t('CANNED_MGMT.IMAGES.LIMITS', {
                count: MAX_CANNED_RESPONSE_IMAGES,
                size: maxSize,
              })
            }}
          </p>
        </div>
        <div class="flex justify-end gap-2 py-2">
          <NextButton
            type="button"
            faded
            slate
            :label="t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            :disabled="isSaving"
            @click="onClose"
          />
          <NextButton
            type="submit"
            :label="t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="v$.$invalid || isSaving"
            :is-loading="isSaving"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>
